import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { FormProvider, useForm } from 'react-hook-form';
import { EventSchema, type Event } from 'shared/modelTypes/event';
import { emitMessage } from 'engine/api';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { EventCreateForm } from 'forms/EventForm';
import { appConfig } from 'engine/appConfig';

export const Route = createFileRoute('/dashboard/events/new')({
	component: RouteComponent,
	loader: async () => {
		return {
			// TODO: at some point we might want to create an API endpoint that only returns the game titles and ids
			gameList: await emitMessage({ kind: 'game-list' }),
			// TODO: at some point we might want to create an API endpoint only returns the variant titles and ids given a game id
			variantList: await emitMessage({ kind: 'variant-list' }),
		};
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	const gameCount = data.gameList.games?.length ?? 0;

	return (
		<div>
			{gameCount <= 0 && (
				<div className="flex flex-col gap-2">
					<span>You must create a game before you can create an event.</span>
					<Button to="/dashboard/games/new">Create game</Button>
				</div>
			)}
			{gameCount > 0 && (
				<EventNewForm
					gameOptions={
						data.gameList.games
							?.map((g) => ({ value: g.id, label: g.game.name }))
							.sort((a, b) => a.label.localeCompare(b.label)) ?? []
					}
					variantOptions={
						appConfig.permissions.allowVariants
							? (data.variantList.variants
									?.map((v) => ({ gameId: v.gameId, value: v.id, label: v.variant.name }))
									.sort((a, b) => a.label.localeCompare(b.label)) ?? [])
							: []
					}
				/>
			)}
		</div>
	);
}

type EventNewFormProps = {
	variantOptions: { gameId: string; value: string; label: string }[];
	gameOptions: { value: string; label: string }[];
};

function EventNewForm(props: EventNewFormProps) {
	const router = useRouter();
	const methods = useForm<Event>({
		// @ts-expect-error zodResolver type inference seems to be bad
		resolver: zodResolver(EventSchema),
		defaultValues: {
			gameId: props.gameOptions[0]?.value,
			variantId: props.variantOptions[0]?.value,
			status: 'open',
			state: { autoEnd: true },
		},
	});

	const err = methods.formState.errors;

	const onSubmit = async (data: Event) => {
		const game = await emitMessage({ kind: 'game-find', id: data.gameId });
		const variables = !!game.game?.game.variables.length;
		const response = await emitMessage({ kind: 'event-create', event: { ...data, status: variables ? 'draft' : data.status } });

		if (response.success) {
			router.invalidate();
			if (response.event) {
				if (variables) {
					router.navigate({
						to: '/dashboard/events/edit/$id',
						params: { id: response.event.id },
						search: { status: data.status !== 'draft' ? data.status : undefined },
					});
				} else {
					router.navigate({
						to: '/dashboard/events/view/$id',
						params: { id: response.event.id },
					});
				}
			} else {
				router.navigate({ to: '/dashboard/events' });
			}
		} else {
			toast('server did not save event', { icon: <X /> });
		}
	};

	console.log(err);

	return (
		<FormProvider {...methods}>
			<form
				className="flex flex-col gap-3"
				// @ts-expect-error zodResolver type inference seems to be bad
				onSubmit={methods.handleSubmit(onSubmit)}
			>
				<EventCreateForm
					getVariantOptions={(gameId: string) => Promise.resolve(props.variantOptions.filter((v) => v.gameId === gameId))}
					gameOptions={props.gameOptions}
					label="New Event"
				/>
				<Button type="submit">Save event</Button>
			</form>
		</FormProvider>
	);
}
